import { UserRole } from '@mm/state/model/CompanyUsersModel';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import {
  currentUserRoleSelector,
  getTwilioAvailable,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { createSelector } from 'reselect';
import {
  SidebarTitleDefinition,
  SIDEBAR_MENU,
  SidebarMenuDefinition,
} from './BotBuilderSidebar/sidebar-menu';

const selectIsSysAdmin = (state: StoreState) => state.session.session.admin;

const getFilteredSidebarMenu = (
  isSysAdmin: boolean,
  featureFlags: ReturnType<typeof combinedFeatureFlagsSelector>,
  isTwillioAvailable: boolean,
  userRole: string,
  isShopify,
) => {
  const smsItemsByFeatureFlag = (section: SidebarTitleDefinition) => {
    if (section.id === 'SMS' && section.featureFlag) {
      return isTwillioAvailable || featureFlags[section.featureFlag];
    }
    return true;
  };

  const byHideFeatureFlag = (section: SidebarTitleDefinition) =>
    !(section.hideFeatureFlag && featureFlags[section.hideFeatureFlag]);

  const automationsByFeatureFlag = (section: SidebarTitleDefinition) =>
    section.featureFlag !== 'automations' || featureFlags.automations;

  const mercuryByFeatureFlag = (section: SidebarTitleDefinition) =>
    section.featureFlag !== 'mercury' || featureFlags.mercury;

  const filterMenuByShopifyStatus = (section: SidebarTitleDefinition) =>
    isShopify
      ? ['LEADS-SEARCH', 'LEADS-CONTACTS', 'INTEGRATIONS'].includes(section.id)
      : true;

  const filterSubMenuByShopifyStatus = (menuItem: SidebarMenuDefinition) =>
    isShopify ? ['XRAY', 'CONTACTS', 'AUDIENCES'].includes(menuItem.id) : true;

  const filteredSideBar = SIDEBAR_MENU.filter(byHideFeatureFlag)
    .filter(smsItemsByFeatureFlag)
    .filter(automationsByFeatureFlag)
    .filter(mercuryByFeatureFlag)
    .filter(filterMenuByShopifyStatus);

  return filteredSideBar.map(sidebarSection => {
    const menuItems = sidebarSection.menuItems
      .filter(menuItem => {
        if (
          menuItem.hideFeatureFlag &&
          !featureFlags[menuItem.hideFeatureFlag]
        ) {
          return false;
        }

        if (menuItem.mustBeSysAdmin) {
          return isSysAdmin;
        }

        if (menuItem.hideForRole?.includes(userRole)) {
          return false;
        }
        return true;
      })
      .filter(filterSubMenuByShopifyStatus);

    return {
      ...sidebarSection,
      menuItems,
    };
  });
};

export const filteredSidebarMenuSelector = createSelector(
  [
    selectIsSysAdmin,
    combinedFeatureFlagsSelector,
    getTwilioAvailable,
    currentUserRoleSelector,
    selectIsShopifyContext,
  ],
  getFilteredSidebarMenu,
);
